import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import {
  ShieldAlert, Clock, Flag, Maximize2, CheckCircle2, AlertTriangle,
  Camera, Mic, Loader2, Send, ChevronLeft, ChevronRight, ZoomIn,
  Hourglass, HelpCircle, Check, Star, LifeBuoy, LayoutGrid
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";

import { useLockdown } from "@/components/secureExam/useLockdown";
import { useFaceDetector, useMicSpike } from "@/components/secureExam/useProctoring";

const mdComponents = {
  p: ({children}) => <span>{children}</span>,
  strong: ({children}) => <strong className="font-semibold">{children}</strong>,
};

function formatDuration(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${String(m).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}

function formatHMS(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rem = s % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}

export default function SecureExamRunner() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [phase, setPhase] = useState("instructions"); // instructions | overview | permissions | running | submitting | submitted
  const [state, setState] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQIdx, setCurrentQIdx] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [showPalette, setShowPalette] = useState(() => typeof window !== "undefined" && window.innerWidth >= 768);
  const [showMarkedOnly, setShowMarkedOnly] = useState(false);
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  const [helpMessage, setHelpMessage] = useState("");
  const [reportReasons, setReportReasons] = useState([]);
  const [sendingHelp, setSendingHelp] = useState(false);

  const REPORT_REASONS = [
    "Not in English or has very bad formatting",
    "Substantially incorrect question and/or incorrect answers",
    "Answer doesn't address the question that was asked",
    "Missing data and/or information",
    "Bad Image",
    "Spam. Undisclosed promotion for a link or product"
  ];

  const toggleReason = (r) => {
    setReportReasons(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);
  };

  const sendHelpRequest = async (questionId) => {
    if (reportReasons.length === 0 && !helpMessage.trim()) {
      toast.error("Select at least one reason or write an explanation");
      return;
    }
    setSendingHelp(true);
    try {
      await axios.post(`${API}/secure-exams/${examId}/report-question`,
        { question_id: questionId, reasons: reportReasons, explanation: helpMessage.trim() },
        { headers });
      toast.success("Report submitted. Your tutor has been notified — keep going!");
      setShowHelpDialog(false);
      setHelpMessage("");
      setReportReasons([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't submit the report. Please try again.");
    } finally { setSendingHelp(false); }
  };
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [pausedBanner, setPausedBanner] = useState(false);
  const [broadcasts, setBroadcasts] = useState([]);
  const [mediaStream, setMediaStream] = useState(null);
  const [warningToast, setWarningToast] = useState(null);
  const [zoomImage, setZoomImage] = useState(null);
  const videoRef = useRef(null);
  const wsRef = useRef(null);
  const initRef = useRef(false);

  // Load preview + attempt state on mount
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    (async () => {
      let pv = null;
      try {
        const r = await axios.get(`${API}/secure-exams/${examId}/preview`, { headers });
        pv = r.data;
        setPreview(pv);
      } catch (e) { /* preview optional */ }
      try {
        // Try to fetch existing attempt first
        const res = await axios.get(`${API}/secure-exams/${examId}/attempt`, { headers });
        if (res.data.status === "in_progress") {
          setState(res.data);
          setPhase("instructions");
        } else if (pv?.practice_mode) {
          // Practice mode: previous attempt finished — allow a fresh retake
          setState(null);
          setPhase("instructions");
        } else {
          setPhase("submitted");
          navigate(`/secure-exam/${examId}/result`, { replace: true });
        }
      } catch (e) {
        // No attempt yet — show instruction screens (attempt will be created on Attempt Test)
        setState(null);
        setPhase("instructions");
      } finally { setLoading(false); }
    })();
  }, [examId, headers, navigate]);

  const beginTimer = async () => {
    try {
      const res = await axios.post(`${API}/secure-exams/${examId}/begin`, {}, { headers });
      setState(prev => prev ? { ...prev, ...res.data } : prev);
    } catch (e) { /* timer already running or attempt gone */ }
  };

  const startAttempt = async () => {
    if (state?.attempt_id) {
      if (state?.practice) { await beginTimer(); setPhase("running"); } else { setPhase("permissions"); }
      return;
    }
    try {
      const res = await axios.post(`${API}/secure-exams/${examId}/start`, {}, { headers });
      setState(res.data);
      if (res.data.practice) { await beginTimer(); setPhase("running"); } else { setPhase("permissions"); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start exam");
    }
  };

  // Request camera + mic permissions
  const requestMedia = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: state?.settings?.face_detection_enabled ? { width: 320, height: 240 } : false,
        audio: state?.settings?.mic_spike_enabled || false
      });
      setMediaStream(s);
      if (videoRef.current && s.getVideoTracks().length > 0) {
        videoRef.current.srcObject = s;
        await videoRef.current.play().catch(() => {});
      }
      await enterFullscreen();
      await beginTimer();
      setPhase("running");
    } catch (e) {
      toast.error("Camera/mic permissions required to start the exam");
    }
  };

  // ==================== WebSocket for teacher commands ====================
  useEffect(() => {
    if (phase !== "running" || !state?.attempt_id) return;
    const wsUrl = `${API.replace(/^http/, "ws")}/ws/secure-exam-student/${examId}/${state.attempt_id}`;
    const ws = new WebSocket(wsUrl.replace(/\/api\/ws/, "/api/ws"));
    wsRef.current = ws;
    ws.onopen = () => {
      ws.send(JSON.stringify({ token }));
    };
    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.type === "paused") {
          setPausedBanner(true);
          toast.warning("Exam paused by teacher");
        } else if (msg.type === "resumed") {
          setPausedBanner(false);
          toast.success("Exam resumed");
          if (msg.time_ends_at) {
            setState(prev => prev ? { ...prev, time_ends_at: msg.time_ends_at, section_timers: msg.section_timers || prev.section_timers } : prev);
          }
        } else if (msg.type === "broadcast") {
          setBroadcasts(prev => [...prev, { message: msg.message, at: msg.at }]);
          toast.info(msg.message, { duration: 8000 });
        } else if (msg.type === "kicked") {
          toast.error(msg.reason || "You have been kicked from the exam");
          setPhase("submitted");
          setTimeout(() => navigate(`/secure-exam/${examId}/result`, { replace: true }), 1500);
        }
      } catch (e) { /* ignore */ }
    };
    ws.onerror = () => { /* silent */ };
    return () => { try { ws.close(); } catch (e) { /* noop */ } };
  }, [phase, state?.attempt_id, examId, token, navigate]);

  // ==================== Tick every second ====================
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // ==================== Server heartbeat every 15s (timer sync) ====================
  useEffect(() => {
    if (phase !== "running") return;
    const beat = async () => {
      try {
        const res = await axios.post(`${API}/secure-exams/${examId}/heartbeat`, {}, { headers });
        if (res.data.time_ends_at) {
          setState(prev => prev ? { ...prev, time_ends_at: res.data.time_ends_at, paused: res.data.paused, section_timers: res.data.section_timers, current_section: res.data.current_section } : prev);
        }
        if (res.data.status && res.data.status !== "in_progress") {
          setPhase("submitted");
          navigate(`/secure-exam/${examId}/result`, { replace: true });
        }
      } catch (e) { /* ignore */ }
    };
    const t = setInterval(beat, 15000);
    return () => clearInterval(t);
  }, [phase, examId, headers, navigate]);

  // ==================== Incident reporter ====================
  const reportIncident = useCallback(async (type, meta = {}) => {
    if (!state?.attempt_id || state?.practice) return;
    try {
      const res = await axios.post(`${API}/secure-exams/${examId}/incident`, { type, meta }, { headers });
      const cnt = res.data?.warnings?.[type] || 0;
      setWarningToast({ type, count: cnt });
      if (res.data.kicked) {
        toast.error(`You have been auto-submitted after ${cnt} ${type.replace("_", " ")} warnings.`);
        setPhase("submitted");
        setTimeout(() => navigate(`/secure-exam/${examId}/result`, { replace: true }), 1500);
      } else {
        toast.warning(`Warning ${cnt}: ${type.replace("_", " ")} detected`);
      }
    } catch (e) { /* ignore */ }
  }, [state?.attempt_id, examId, headers, navigate]);

  // ==================== Lockdown ====================
  const { enterFullscreen } = useLockdown({
    enabled: phase === "running" && !!state?.settings?.fullscreen_required,
    onTabSwitch: () => reportIncident("tab_switch"),
    onFullscreenExit: () => reportIncident("fullscreen_exit"),
    onCopyAttempt: () => reportIncident("copy_attempt"),
    onRightClick: () => reportIncident("right_click"),
    onKeyboardBlock: (k) => reportIncident("copy_attempt", { key: k })
  });

  // ==================== Proctoring ====================
  useFaceDetector({
    enabled: phase === "running" && !!state?.settings?.face_detection_enabled && !!mediaStream,
    videoRef,
    onNoFace: () => reportIncident("no_face"),
    onMultiFace: (count) => reportIncident("multi_face", { count })
  });
  useMicSpike({
    enabled: phase === "running" && !!state?.settings?.mic_spike_enabled && !!mediaStream,
    stream: mediaStream,
    onSpike: (avg) => reportIncident("mic_spike", { avg })
  });

  // ==================== Answer save (debounced per Q) ====================
  const saveAnswer = useCallback(async (qid, val) => {
    try {
      await axios.post(`${API}/secure-exams/${examId}/answer`, { question_id: qid, answer: val }, { headers });
      setState(prev => prev ? { ...prev, answers: { ...(prev.answers || {}), [qid]: val } } : prev);
    } catch (e) { /* ignore */ }
  }, [examId, headers]);

  const toggleFlag = useCallback(async (qid) => {
    // Optimistic update so the UI + submit summary always reflect the mark instantly
    setState(prev => {
      if (!prev) return prev;
      const cur = prev.flags || [];
      const next = cur.includes(qid) ? cur.filter(x => x !== qid) : [...cur, qid];
      return { ...prev, flags: next };
    });
    try {
      const res = await axios.post(`${API}/secure-exams/${examId}/flag`, { question_id: qid }, { headers });
      setState(prev => prev ? { ...prev, flags: res.data.flags } : prev);
    } catch (e) {
      toast.error("Couldn't update the review mark. Please try again.");
      setState(prev => {
        if (!prev) return prev;
        const cur = prev.flags || [];
        const next = cur.includes(qid) ? cur.filter(x => x !== qid) : [...cur, qid];
        return { ...prev, flags: next };
      });
    }
  }, [examId, headers]);

  const advanceSection = useCallback(async (target, qIdx = 0) => {
    try {
      const res = await axios.post(`${API}/secure-exams/${examId}/section`, { section_index: target }, { headers });
      setState(prev => prev ? { ...prev, current_section: res.data.current_section, section_timers: res.data.section_timers } : prev);
      setCurrentQIdx(qIdx);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't switch section");
    }
  }, [examId, headers]);

  const doSubmit = async () => {
    setPhase("submitting");
    try {
      await axios.post(`${API}/secure-exams/${examId}/submit`, {}, { headers });
      try { if (document.fullscreenElement) await document.exitFullscreen(); } catch (e) { /* noop */ }
      mediaStream?.getTracks().forEach(t => t.stop());
      navigate(`/secure-exam/${examId}/result`, { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submit failed");
      setPhase("running");
    }
  };

  // ==================== Auto-submit on master timer expiry ====================
  useEffect(() => {
    if (phase !== "running" || !state?.time_ends_at) return;
    const ends = new Date(state.time_ends_at).getTime();
    if (ends - now <= 0 && !state.paused) {
      toast.info("Time up! Submitting your paper...");
      doSubmit();
    }
  }, [now, state?.time_ends_at, state?.paused, phase]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  // ==================== SCREEN 1: GENERAL INSTRUCTIONS ====================
  if (phase === "instructions") {
    const SECURE_ONLY_INSTRUCTIONS = [
      "The test must be completed in one attempt. Once submitted, it cannot be re-attempted or restarted.",
      "If the app is closed or the screen is changed more than three times, the test will be submitted automatically."
    ];
    const generalInstructions = (preview?.general_instructions || []).filter(
      ins => !(preview?.practice_mode && SECURE_ONLY_INSTRUCTIONS.includes(ins))
    );
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="secure-exam-instructions">
        <div className="bg-slate-900 text-white px-4 py-3 flex items-center gap-3 shrink-0">
          <button onClick={() => navigate(-1)} className="p-1 rounded hover:bg-slate-800" data-testid="instructions-back-btn">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <ShieldAlert className="w-5 h-5 text-amber-400 shrink-0" />
          <div className="text-sm font-semibold truncate">{preview?.title || state?.title || "Secure Exam"}</div>
        </div>
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-2xl mx-auto px-4 py-6 pb-28">
            <h1 className="text-lg font-bold text-slate-900 mb-1">General Instructions</h1>
            <p className="text-xs text-slate-500 mb-5">Read all instructions carefully before proceeding.</p>
            {preview?.practice_mode && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs text-emerald-800 mb-4" data-testid="practice-instructions-note">
                <strong>Practice Mode:</strong> unlimited retakes, no lockdown or auto-submit penalties. Correct answers are shown after each attempt.
              </div>
            )}
            <ol className="space-y-4" data-testid="general-instructions-list">
              {generalInstructions.map((ins, i) => (
                <li key={i} className="flex gap-3 text-sm text-slate-700 leading-relaxed">
                  <span className="shrink-0 w-6 h-6 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold flex items-center justify-center mt-0.5">{i + 1}</span>
                  <span className="flex-1">{ins}</span>
                </li>
              ))}
              {generalInstructions.length === 0 && (
                <li className="text-sm text-slate-500 flex items-center"><Loader2 className="w-4 h-4 animate-spin mr-2" />Loading instructions...</li>
              )}
            </ol>
          </div>
        </div>
        <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 p-4">
          <div className="max-w-2xl mx-auto">
            <Button
              onClick={() => setPhase("overview")}
              className="w-full h-12 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
              data-testid="instructions-next-btn"
            >
              Next<ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ==================== SCREEN 2: TEST OVERVIEW ====================
  if (phase === "overview") {
    const alreadyStarted = !!state?.attempt_id;
    const sectionColors = ["bg-rose-700", "bg-amber-700", "bg-emerald-700", "bg-sky-700", "bg-violet-700", "bg-slate-700"];
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="secure-exam-overview">
        <div className="bg-slate-900 text-white px-4 py-3 shrink-0">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center gap-3">
              <button onClick={() => setPhase("instructions")} className="p-1 rounded hover:bg-slate-800" data-testid="overview-back-btn">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <ShieldAlert className="w-5 h-5 text-amber-400 shrink-0" />
              <div className="text-sm font-semibold truncate">{preview?.title || state?.title || "Secure Exam"}</div>
            </div>
            <div className="flex items-center justify-around mt-3 pb-1 text-center">
              <div>
                <div className="text-base font-bold" data-testid="overview-question-count">{preview?.question_count ?? "—"}</div>
                <div className="text-[10px] text-slate-400">Question(s)</div>
              </div>
              <div>
                <div className="text-base font-bold" data-testid="overview-duration">{preview?.duration_minutes ?? "—"}</div>
                <div className="text-[10px] text-slate-400">Minutes</div>
              </div>
              <div>
                <div className="text-base font-bold" data-testid="overview-total-marks">{preview?.total_marks ?? "—"}</div>
                <div className="text-[10px] text-slate-400">Marks</div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-2xl mx-auto px-4 py-6 pb-28 space-y-6">
            <div>
              <h2 className="text-base font-bold text-slate-900 mb-3">Test Instructions</h2>
              <ul className="space-y-2.5" data-testid="test-instructions-list">
                {(preview?.test_instructions || []).map((ins, i) => (
                  <li key={i} className="text-sm font-medium text-slate-800 leading-relaxed">{ins}</li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900 mb-3">Test Sections</h2>
              <div className="space-y-3" data-testid="test-sections-list">
                {(preview?.sections || []).map((s, i) => (
                  <div key={i} className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-3" data-testid={`overview-section-${i}`}>
                    <div className={`w-9 h-9 rounded-full ${sectionColors[i % sectionColors.length]} text-white flex items-center justify-center text-sm font-bold shrink-0`}>{i + 1}</div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900 truncate">{s.name}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-3 mt-0.5">
                        <span>{s.question_count} Questions</span>
                        <span>{s.marks} Marks</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {preview?.practice_mode && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs text-emerald-800" data-testid="practice-mode-note">
                <strong>Practice Mode:</strong> you can retake this test as many times as you like. No webcam, fullscreen or tab-switch lockdown applies. Your best score is saved.
              </div>
            )}
          </div>
        </div>
        <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 p-4">
          <div className="max-w-2xl mx-auto">
            <Button
              onClick={startAttempt}
              className="w-full h-12 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
              data-testid="start-exam-btn"
            >
              {alreadyStarted ? "Resume Exam" : preview?.practice_mode ? "Attempt Practice Test" : "Attempt Test"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ==================== PERMISSIONS ====================
  if (phase === "permissions") {
    const needCam = !!state?.settings?.face_detection_enabled;
    const needMic = !!state?.settings?.mic_spike_enabled;
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6" data-testid="secure-exam-permissions">
        <div className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-2xl p-8">
          <h1 className="text-lg font-bold mb-2">Grant Permissions</h1>
          <p className="text-xs text-slate-400 mb-5">The exam needs access to the following to ensure fair play. Video/audio is analysed locally — nothing is uploaded.</p>
          <div className="space-y-3 mb-6">
            {needCam && <div className="flex items-center gap-3 p-3 bg-slate-800 rounded-lg"><Camera className="w-5 h-5 text-amber-400" /><div className="text-sm">Camera — for face detection</div></div>}
            {needMic && <div className="flex items-center gap-3 p-3 bg-slate-800 rounded-lg"><Mic className="w-5 h-5 text-amber-400" /><div className="text-sm">Microphone — for talking detection</div></div>}
            <div className="flex items-center gap-3 p-3 bg-slate-800 rounded-lg"><Maximize2 className="w-5 h-5 text-amber-400" /><div className="text-sm">Fullscreen — will be enforced</div></div>
          </div>
          <Button onClick={requestMedia} className="w-full h-12 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="grant-permissions-btn">
            Grant Access & Start
          </Button>
        </div>
      </div>
    );
  }

  if (phase === "submitting" || phase === "submitted") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-amber-400 mx-auto mb-3" />
          <p>Finalising your submission...</p>
        </div>
      </div>
    );
  }

  // ==================== RUNNING (main exam UI) ====================
  const questions = state?.questions || [];
  const currentSection = state?.current_section ?? 0;
  const sectionQs = questions.filter(q => q.section_index === currentSection);
  const currentQ = sectionQs[Math.max(0, Math.min(currentQIdx, sectionQs.length - 1))];
  const answers = state?.answers || {};
  const flags = state?.flags || [];

  const masterEnds = state?.time_ends_at ? new Date(state.time_ends_at).getTime() : 0;
  const secEnds = state?.section_timers?.[currentSection]?.ends_at
    ? new Date(state.section_timers[currentSection].ends_at).getTime() : 0;
  const masterRem = Math.max(0, Math.floor((masterEnds - now) / 1000));
  const secRem = Math.max(0, Math.floor((secEnds - now) / 1000));

  const answeredInSection = sectionQs.filter(q => answers[q.question_id] !== undefined && answers[q.question_id] !== "").length;
  const allowJump = state?.settings?.allow_section_jump !== false;
  const prevSectionQs = currentSection > 0 ? questions.filter(q => q.section_index === currentSection - 1) : [];
  const canGoPrev = currentQIdx > 0 || (currentSection > 0 && allowJump);
  const goPrev = () => {
    if (currentQIdx > 0) { setCurrentQIdx(currentQIdx - 1); return; }
    if (currentSection > 0 && allowJump) {
      advanceSection(currentSection - 1, Math.max(0, prevSectionQs.length - 1));
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 flex flex-col" data-testid="secure-exam-runner">
      {/* Top bar — timers on the left, submit on the right */}
      <div className="bg-slate-900 text-white px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className={`px-3 py-1.5 rounded-lg text-xs font-mono ${masterRem <= 300 ? "bg-red-600" : "bg-slate-800"}`} data-testid="master-timer">
            <Clock className="w-3 h-3 inline mr-1" />Total: {formatDuration(masterRem)}
          </div>
          <div className={`px-3 py-1.5 rounded-lg text-xs font-mono ${secRem <= 60 ? "bg-red-600" : secRem <= 180 ? "bg-orange-500" : "bg-slate-800"}`} data-testid="section-timer">
            Section: {formatDuration(secRem)}
          </div>
        </div>
        <div className="flex items-center gap-3 min-w-0 flex-1 justify-center">
          <ShieldAlert className="w-5 h-5 text-amber-400 shrink-0 hidden sm:block" />
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate flex items-center gap-2">
              {state?.title}
              {state?.practice && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500 text-white font-bold uppercase" data-testid="practice-badge">Practice</span>}
            </div>
            <div className="text-[10px] text-slate-400 truncate">Section {currentSection + 1}/{state?.sections?.length}: {state?.sections?.[currentSection]?.name}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowPalette(v => !v)}
            className="md:hidden h-8 w-8 rounded-lg border border-slate-600 flex items-center justify-center text-slate-200 hover:bg-slate-800"
            data-testid="palette-toggle-btn"
            aria-label="Question palette"
          >
            <LayoutGrid className="w-4 h-4" />
          </button>
          <Button size="sm" variant="outline" onClick={() => setShowHelpDialog(true)} data-testid="report-problem-btn" className="h-8 bg-transparent border-slate-600 text-slate-200 hover:bg-slate-800 hover:text-white">
            <AlertTriangle className="w-3.5 h-3.5 mr-1" />Report
          </Button>
          <Button size="sm" variant="destructive" onClick={() => setShowSubmitConfirm(true)} data-testid="submit-exam-btn" className="h-8">
            <Send className="w-3.5 h-3.5 mr-1" />Submit
          </Button>
        </div>
      </div>

      {/* Paused overlay */}
      {pausedBanner && (
        <div className="fixed inset-0 bg-slate-950/90 z-50 flex items-center justify-center">
          <div className="text-center text-white">
            <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-3" />
            <div className="text-lg font-semibold">Exam Paused by Teacher</div>
            <div className="text-xs text-slate-400 mt-1">Your timer is frozen. Please wait...</div>
          </div>
        </div>
      )}

      {/* Section tabs */}
      <div className="bg-white border-b border-slate-200 px-4 py-2 flex items-center gap-2 overflow-x-auto">
        {state?.sections?.map((s, i) => {
          const timerStarted = state?.section_timers?.[i]?.started_at;
          const isCurrent = i === currentSection;
          const jumpLocked = !allowJump && !isCurrent;
          return (
            <button
              key={i}
              onClick={() => advanceSection(i)}
              disabled={jumpLocked || (!timerStarted && i > currentSection && !state?.section_timers?.[i-1]?.ends_at)}
              title={jumpLocked ? "Section jumping is disabled for this exam" : undefined}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${isCurrent ? "bg-amber-500 text-slate-950" : jumpLocked ? "bg-slate-50 text-slate-400 cursor-not-allowed" : "bg-slate-100 hover:bg-slate-200 text-slate-700"}`}
              data-testid={`section-tab-${i}`}
            >
              {jumpLocked && <span className="mr-1">🔒</span>}{i + 1}. {s.name}
            </button>
          );
        })}
      </div>

      {/* Broadcast banners */}
      {broadcasts.length > 0 && (
        <div className="bg-amber-100 border-b border-amber-300 px-4 py-2 text-xs text-amber-900">
          <strong>📢 Announcement:</strong> {broadcasts[broadcasts.length - 1].message}
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Question area */}
        <div className="flex-1 p-4 md:p-6 overflow-y-auto">
          {currentQ ? (
            <div className="max-w-3xl mx-auto">
              {/* Question number strip (mobile) — current section */}
              <div className="md:hidden flex items-center gap-1.5 overflow-x-auto pb-2 mb-3" data-testid="question-number-strip">
                {sectionQs.map((q, i) => {
                  const isAnswered = answers[q.question_id] !== undefined && answers[q.question_id] !== "";
                  const isFlagged = flags.includes(q.question_id);
                  const isCur = i === currentQIdx;
                  const bg = isCur ? "bg-slate-900 text-white" :
                    isFlagged && isAnswered ? "bg-purple-500 text-white" :
                    isFlagged ? "bg-yellow-400 text-slate-900" :
                    isAnswered ? "bg-green-500 text-white" :
                    "bg-white border border-slate-300 text-slate-700";
                  return (
                    <button key={q.question_id} onClick={() => setCurrentQIdx(i)}
                      className={`w-8 h-8 rounded-full text-[11px] font-semibold shrink-0 ${bg}`}
                      data-testid={`strip-q-${i}`}>
                      {i + 1}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm text-slate-500">
                  Question {currentQIdx + 1} of {sectionQs.length} · <span className="font-medium text-slate-700">{currentQ.marks} marks</span>
                </div>
                <button
                  onClick={() => toggleFlag(currentQ.question_id)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${flags.includes(currentQ.question_id) ? "bg-yellow-100 border-yellow-400 text-yellow-800" : "border-slate-300 hover:bg-slate-100"}`}
                  data-testid={`flag-btn-${currentQ.question_id}`}
                >
                  <Flag className="w-3 h-3 inline mr-1" />
                  {flags.includes(currentQ.question_id) ? "Marked for Review" : "Mark for Review"}
                </button>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5 md:p-6 mb-4">
                <div className="text-base md:text-lg leading-relaxed mb-4 prose max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]} components={mdComponents}>
                    {currentQ.text}
                  </ReactMarkdown>
                </div>
                {currentQ.image_url && (
                  <div className="mb-4 relative inline-block">
                    <img src={currentQ.image_url} alt="Question diagram" className="max-w-full max-h-72 rounded-lg border border-slate-200 cursor-zoom-in" onClick={() => setZoomImage(currentQ.image_url)} />
                    <button className="absolute top-2 right-2 bg-white/90 rounded-full p-1.5 shadow" onClick={() => setZoomImage(currentQ.image_url)}>
                      <ZoomIn className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}

                {/* MCQ */}
                {currentQ.type === "mcq" && currentQ.options && (
                  <div className="space-y-2">
                    {currentQ.options.map((opt, i) => (
                      <label key={i} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${answers[currentQ.question_id] === String(i) ? "border-amber-500 bg-amber-50" : "border-slate-200 hover:bg-slate-50"}`}>
                        <input
                          type="radio"
                          name={`q-${currentQ.question_id}`}
                          value={i}
                          checked={answers[currentQ.question_id] === String(i)}
                          onChange={() => saveAnswer(currentQ.question_id, String(i))}
                          onClick={() => { if (answers[currentQ.question_id] === String(i)) saveAnswer(currentQ.question_id, ""); }}
                          className="mt-1"
                          data-testid={`option-${currentQ.question_id}-${i}`}
                        />
                        <span className="text-sm flex-1">
                          <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]} components={mdComponents}>{opt}</ReactMarkdown>
                        </span>
                      </label>
                    ))}
                    {answers[currentQ.question_id] !== undefined && answers[currentQ.question_id] !== "" && (
                      <button
                        onClick={() => saveAnswer(currentQ.question_id, "")}
                        className="text-xs text-red-600 hover:text-red-700 underline underline-offset-2 mt-1"
                        data-testid="clear-response-btn"
                      >
                        Clear Response
                      </button>
                    )}
                  </div>
                )}

                {/* Numerical */}
                {currentQ.type === "numerical" && (
                  <div>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={answers[currentQ.question_id] || ""}
                      onChange={(e) => saveAnswer(currentQ.question_id, e.target.value)}
                      placeholder="Enter your numerical answer"
                      className="w-full max-w-xs px-4 py-2.5 rounded-lg border border-slate-300 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200 text-lg"
                      data-testid={`numerical-input-${currentQ.question_id}`}
                    />
                    <p className="text-[10px] text-slate-500 mt-1">Numerical answers accept decimal values.</p>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between">
                <Button variant="outline" size="sm" onClick={goPrev} disabled={!canGoPrev} data-testid="prev-q-btn">
                  <ChevronLeft className="w-4 h-4 mr-1" />Previous
                </Button>
                <div className="text-xs text-slate-500 hidden sm:block">
                  Answered {answeredInSection}/{sectionQs.length} in this section
                </div>
                {(() => {
                  const isLastQInSection = currentQIdx === sectionQs.length - 1;
                  const isLastSection = currentSection === (state?.sections?.length || 1) - 1;
                  if (isLastQInSection && isLastSection) {
                    return (
                      <Button size="sm" onClick={() => setShowSubmitConfirm(true)} data-testid="next-q-btn" className="bg-green-600 hover:bg-green-500 text-white">
                        <Send className="w-4 h-4 mr-1" />Submit Exam
                      </Button>
                    );
                  }
                  if (isLastQInSection && !isLastSection) {
                    return (
                      <Button size="sm" onClick={() => advanceSection(currentSection + 1)} data-testid="next-q-btn" className="bg-amber-500 hover:bg-amber-400 text-slate-950">
                        Next Section: {state?.sections?.[currentSection + 1]?.name}<ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    );
                  }
                  return (
                    <Button size="sm" onClick={() => setCurrentQIdx(currentQIdx + 1)} data-testid="next-q-btn" className="bg-amber-500 hover:bg-amber-400 text-slate-950">
                      Next<ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  );
                })()}
              </div>
            </div>
          ) : (
            <div className="text-center text-slate-500 mt-20">No questions in this section.</div>
          )}
        </div>

        {/* Palette */}
        {showPalette && (
        <aside className="bg-white border-l border-slate-200 overflow-y-auto shrink-0 fixed inset-y-0 right-0 z-40 w-72 shadow-2xl md:static md:w-64 md:z-auto md:shadow-none" data-testid="question-palette">
          <div className="p-3 border-b border-slate-200 flex items-center justify-between">
            <h3 className="text-xs font-semibold text-slate-700">Question Palette</h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowMarkedOnly(v => !v)}
                className={`text-[10px] px-2 py-1 rounded-full border transition-colors ${showMarkedOnly ? "bg-yellow-400 border-yellow-500 text-slate-900 font-semibold" : "border-slate-300 text-slate-500 hover:bg-slate-100"}`}
                data-testid="marked-only-toggle"
              >
                <Flag className="w-2.5 h-2.5 inline mr-0.5" />Marked
              </button>
              <button onClick={() => setShowPalette(false)} className="text-base leading-none px-1.5 text-slate-500 hover:text-slate-700 md:hidden" data-testid="palette-close-btn">×</button>
            </div>
          </div>
          <div className="p-3 space-y-3">
            {state?.sections?.map((s, si) => {
              let sQsAll = questions.filter(q => q.section_index === si);
              const indexed = sQsAll.map((q, i) => ({ q, i }));
              const visible = showMarkedOnly ? indexed.filter(({ q }) => flags.includes(q.question_id)) : indexed;
              if (showMarkedOnly && visible.length === 0) return null;
              return (
                <div key={si}>
                  <div className="text-[10px] font-semibold text-slate-500 uppercase mb-1.5">{s.name}</div>
                  <div className="grid grid-cols-5 gap-1.5">
                    {visible.map(({ q, i }) => {
                      const idxInSec = i;
                      const isAnswered = answers[q.question_id] !== undefined && answers[q.question_id] !== "";
                      const isFlagged = flags.includes(q.question_id);
                      const isCurrent = si === currentSection && idxInSec === currentQIdx;
                      const jumpLocked = !allowJump && si !== currentSection;
                      const bg = isCurrent ? "bg-slate-900 text-white" :
                        jumpLocked ? "bg-slate-50 text-slate-300 cursor-not-allowed" :
                        isFlagged && isAnswered ? "bg-purple-500 text-white" :
                        isFlagged ? "bg-yellow-400 text-slate-900" :
                        isAnswered ? "bg-green-500 text-white" :
                        "bg-slate-100 text-slate-700";
                      return (
                        <button
                          key={q.question_id}
                          onClick={() => advanceSection(si, idxInSec)}
                          disabled={jumpLocked}
                          className={`h-8 rounded text-[11px] font-medium ${bg} ${jumpLocked ? "" : "hover:opacity-80"}`}
                          data-testid={`palette-${si}-${idxInSec}`}
                        >
                          {idxInSec + 1}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {showMarkedOnly && flags.length === 0 && (
              <p className="text-[11px] text-slate-400 text-center py-4">No questions marked for review yet.</p>
            )}
          </div>
          <div className="p-3 border-t border-slate-200 space-y-1 text-[10px] text-slate-600">
            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-green-500"></span>Answered</div>
            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-yellow-400"></span>Marked for Review</div>
            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-purple-500"></span>Answered & Marked</div>
            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-slate-100 border border-slate-300"></span>Not Answered</div>
          </div>
        </aside>
        )}
      </div>

      {/* Webcam preview (mini, corner) */}
      {mediaStream && state?.settings?.face_detection_enabled && (
        <div className="fixed bottom-4 right-4 w-32 h-24 rounded-lg overflow-hidden border-2 border-slate-800 bg-black z-40 shadow-lg">
          <video ref={videoRef} className="w-full h-full object-cover" muted autoPlay playsInline />
          <div className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
        </div>
      )}

      {/* Warning toast counter (persistent) */}
      {warningToast && (
        <div className="fixed bottom-4 left-4 bg-red-600 text-white px-3 py-2 rounded-lg text-xs shadow-lg z-40" data-testid="warning-badge">
          <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
          Warning #{warningToast.count}: {warningToast.type.replace("_", " ")}
        </div>
      )}

      {/* Report Question */}
      <Dialog open={showHelpDialog} onOpenChange={(o) => { setShowHelpDialog(o); if (!o) { setReportReasons([]); setHelpMessage(""); } }}>
        <DialogContent className="max-w-md" data-testid="help-dialog">
          <DialogHeader>
            <DialogTitle>Report Question</DialogTitle>
            <DialogDescription>Help us understand the problem. Why is the question wrong ?</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {REPORT_REASONS.map((r, i) => (
              <label key={i} className="flex items-start gap-3 cursor-pointer" data-testid={`report-reason-${i}`}>
                <input
                  type="checkbox"
                  checked={reportReasons.includes(r)}
                  onChange={() => toggleReason(r)}
                  className="mt-0.5 w-4 h-4 accent-sky-600"
                  data-testid={`report-reason-check-${i}`}
                />
                <span className="text-sm text-slate-700 leading-snug">{r}</span>
              </label>
            ))}
            <div className="pt-1">
              <div className="text-sm font-bold text-slate-900 mb-1.5">Explanation</div>
              <textarea
                value={helpMessage}
                onChange={(e) => setHelpMessage(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="Give feedback (optional)"
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:border-sky-400 focus:outline-none resize-none"
                data-testid="help-message-input"
              />
            </div>
            <Button
              onClick={() => sendHelpRequest(currentQ?.question_id)}
              disabled={sendingHelp}
              className="w-full h-11 bg-sky-600 hover:bg-sky-500 text-white font-semibold"
              data-testid="send-help-btn"
            >
              {sendingHelp ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Submit confirm */}
      <Dialog open={showSubmitConfirm} onOpenChange={setShowSubmitConfirm}>
        <DialogContent className="max-w-sm" data-testid="submit-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Submit Test</DialogTitle>
            <DialogDescription>Are you sure you want to end this test ?</DialogDescription>
          </DialogHeader>
          {(() => {
            const totalQs = questions.length;
            const answeredCount = Object.keys(answers).filter(k => answers[k] !== undefined && answers[k] !== "").length;
            const markedCount = flags.length;
            const rows = [
              { icon: Hourglass, iconClass: "text-orange-500", bg: "bg-orange-100", label: "Time Remaining", value: formatHMS(masterRem), testid: "summary-time" },
              { icon: HelpCircle, iconClass: "text-sky-500", bg: "bg-sky-100", label: "Total Questions", value: totalQs, testid: "summary-total" },
              { icon: Check, iconClass: "text-green-600", bg: "bg-green-100", label: "Questions Answered", value: answeredCount, testid: "summary-answered" },
              { icon: Star, iconClass: "text-purple-600", bg: "bg-purple-100", label: "Marked for Review", value: markedCount, testid: "summary-marked" }
            ];
            return (
              <div className="space-y-4 py-2" data-testid="submit-summary">
                {rows.map((r) => (
                  <div key={r.label} className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full ${r.bg} flex items-center justify-center shrink-0`}>
                      <r.icon className={`w-5 h-5 ${r.iconClass}`} />
                    </div>
                    <div className="flex-1 text-sm text-slate-700">{r.label}</div>
                    <div className="text-base font-semibold text-slate-900 font-mono" data-testid={r.testid}>{r.value}</div>
                  </div>
                ))}
              </div>
            );
          })()}
          <Button
            onClick={doSubmit}
            className="w-full h-12 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
            data-testid="confirm-submit-btn"
          >
            Submit Test
          </Button>
        </DialogContent>
      </Dialog>

      {/* Zoom dialog */}
      <Dialog open={!!zoomImage} onOpenChange={(o) => !o && setZoomImage(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Diagram</DialogTitle>
            <DialogDescription>Pinch or scroll to inspect. Click outside to close.</DialogDescription>
          </DialogHeader>
          {zoomImage && <img src={zoomImage} alt="zoom" className="w-full max-h-[80vh] object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
